import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Store, Star } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { money } from "@/lib/orderStatus";

// Carte produit style "boutique mode" : grande photo verticale (3:4), badge épuré,
// nom en serif, note en étoiles, prix. Clic sur la carte -> page détail (onOpen).
// L'ajout au panier se fait UNIQUEMENT dans la fiche produit (après choix de la taille).
export const ProductCard = ({ product: p, currency, onOpen, index = 0 }) => {
  const { t } = useTranslation();
  const out = p.stock_qty <= 0;
  const isNew = p.created_at && (Date.now() - new Date(p.created_at).getTime()) < 30 * 86400000;
  const badge = p.popularity >= 4 ? t("store.topSeller") : (isNew ? t("store.newBadge") : null);
  const rating = p.popularity || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.4), duration: 0.4 }}
      className="shop-card group flex flex-col h-full cursor-pointer"
      data-testid={`store-product-${p.product_id}`}
      onClick={() => onOpen?.(p)}
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#F3F3F3] flex items-center justify-center">
        {badge && (
          <span className="absolute top-2 left-2 z-10 px-2.5 py-1 bg-black text-white text-[10px] font-jost font-medium uppercase tracking-wider" data-testid={`badge-${p.product_id}`}>
            {badge}
          </span>
        )}
        {p.image_url ? (
          <img
            src={fileUrl(p.image_url)}
            alt={p.name}
            loading="lazy"
            decoding="async"
            data-testid={`product-image-${p.product_id}`}
            className="shop-card-img h-full w-full object-contain object-center"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center"><Store className="h-8 w-8 text-[#cfc8ba]" /></div>
        )}
        {out && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <span className="bg-black text-white text-xs font-jost uppercase tracking-widest px-3 py-1.5" data-testid={`out-of-stock-${p.product_id}`}>{t("store.soldOut")}</span>
          </div>
        )}
      </div>

      <div className="pt-3 flex flex-col flex-1">
        <h3 className="font-serif-display text-base md:text-lg font-medium leading-snug text-[#1A1A1A] line-clamp-2 min-h-[2.75rem] md:min-h-[3.25rem]" data-testid={`product-name-${p.product_id}`}>{p.name}</h3>
        <div className="flex items-center gap-0.5 mt-1" data-testid={`rating-${p.product_id}`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={`h-3 w-3 ${rating >= n ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#D9D9D9]"}`} />
          ))}
        </div>
        <p className="font-jost text-sm md:text-base text-[#1A1A1A] mt-1.5 font-medium" data-testid={`product-price-${p.product_id}`}>{money(p.price, currency)}</p>

        <div className="mt-auto pt-3">
          <button
            data-testid={`view-${p.product_id}`}
            onClick={(e) => { e.stopPropagation(); onOpen?.(p); }}
            disabled={out}
            className="w-full h-9 bg-black text-white font-jost text-xs uppercase tracking-widest hover:bg-[#333] transition-colors disabled:opacity-40"
          >
            {out ? t("store.soldOut") : t("store.viewProduct")}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
